import { Check, Close, Email } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Alert, Avatar, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, List, ListItem, ListItemAvatar, ListItemText, Stack, Tooltip, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSendEmailToAramexMutation } from 'src/_api/mapitShipApi'

export default function SendEmailToAramexDialog({ shipments }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const shipmentsWithMeta = useMemo(() => {
    if (!Array.isArray(shipments)) {
      return []
    }

    return shipments.map((order, index) => {
      const shipment = order?.otoIntegration?.returnShipment ?? order?.otoIntegration?.shipment ?? {}
      const deliveryOptionName = shipment?.deliveryOption?.deliveryOptionName ?? ''
      const isAramex = typeof deliveryOptionName === 'string' && deliveryOptionName.toLowerCase().includes('aramex')

      return {
        id: order?.id ?? order?.orderNumber ?? index,
        deliveryOptionName,
        logo: shipment?.deliveryOption?.logo,
        orderNumber: order?.orderNumber,
        trackingNumber: shipment?.trackingNumber,
        isAramex
      }
    })
  }, [shipments])

  const hasShipments = shipmentsWithMeta.length > 0
  const hasAramexShipments = shipmentsWithMeta.some(item => item.isAramex)
  const hasShipmentsFromAnotherCompany = shipmentsWithMeta.some(item => !item.isAramex)

  const [sendEmailToAramex, { isLoading, isSuccess, error, reset }] = useSendEmailToAramexMutation()

  const handleSendEmail = async () => {
    try {
      const orderIds = shipmentsWithMeta.filter(item => item.isAramex).map(item => item.id)
      await sendEmailToAramex({ orderIds }).unwrap()

      setTimeout(() => {
        setOpen(false)
      }, 3000);
    } catch (error) {
      console.log('Failed to send email to Aramex:', error)
    }
  }

  useEffect(() => {
    if (!open) {
      reset()
    }
  }, [open])

  return (
    <>
    <Tooltip placement='top' title={t("Email Aramex about delayed shipments")}>
      <Button
        variant='contained'
        startIcon={
          <Avatar
            src='https://storage.googleapis.com/mapit-staging/1739874208121.png'
            alt={t('Aramex')}
            sx={{ width: 30, height: 30, objectFit: 'cover' }}
          />
        }
        sx={{ textTransform: 'none' }}
        onClick={() => setOpen(true)}
        disabled={!shipments || shipments.length === 0}
      >
        {t('Email')}
      </Button>
      </Tooltip>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>
          {t('Email Aramex about delayed shipments')}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3 }}>
          {hasShipments ? (
            <Stack spacing={2}>
              {hasShipmentsFromAnotherCompany && (
                <Alert severity='info' variant='outlined'>
                  {t("Only late shipments from Aramex can be sent by email.")}
                </Alert>
              )}
              <List disablePadding>
                {shipmentsWithMeta.map((item, index) => {
                  const secondaryDetails = []

                  if (item.orderNumber) {
                    secondaryDetails.push(
                      <Typography key='orderNumber' variant='body2' color='text.secondary'>
                        {t('Order')} #{item.orderNumber}
                      </Typography>
                    )
                  }

                  if (item.trackingNumber) {
                    secondaryDetails.push(
                      <Typography key='trackingNumber' variant='body2' color='text.secondary'>
                        {t('Tracking')} #{item.trackingNumber}
                      </Typography>
                    )
                  }

                  return (
                    <ListItem
                      key={item.id ?? index}
                      disableGutters
                      alignItems='flex-start'
                      secondaryAction={
                        <Box mr={1}>
                          <Tooltip
                            title={item.isAramex ? t('Aramex') : t("This shipment is not assigned to Aramex.")}
                            placement='top'
                          >
                            {item.isAramex ? <Check color='success' /> : <Close color='error' />}
                          </Tooltip>
                        </Box>
                      }
                      sx={{
                        mb: 1.5,
                        px: 2.2,
                        py: 1.5,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: item.isAramex ? 'primary.main' : 'error.main',
                        opacity: item.isAramex ? 1 : 0.5,
                        transition: theme => theme.transitions.create(['background-color', 'opacity'], {
                          duration: theme.transitions.duration.shortest
                        })
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          variant='rounded'
                          src={item.logo}
                          alt={item.deliveryOptionName || 'shipment-logo'}
                        >
                          {item.deliveryOptionName ? item.deliveryOptionName.charAt(0).toUpperCase() : '?'}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant='subtitle1' noWrap>
                            {item.deliveryOptionName || t('Unknown shipping company')}
                          </Typography>
                        }
                        secondary={
                          secondaryDetails.length ? (
                            <Stack spacing={0.25} mt={0.75}>
                              {secondaryDetails}
                            </Stack>
                          ) : null
                        }
                      />
                    </ListItem>
                  )
                })}
              </List>
            </Stack>
          ) : (
            <Alert severity='info' variant='outlined'>
              {t('No shipments selected.')}
            </Alert>
          )}
        </DialogContent>
        <Divider />
        <DialogActions>
          {isSuccess && <Typography color="success.main">{t("Email sent successfully to Aramex.")}</Typography>}
          {!!error && <Typography color="error">{error}</Typography>}
          <Box sx={{ mr: "auto" }} />
          <Button
            variant='contained'
            sx={{ textTransform: 'none' }}
            onClick={() => setOpen(false)}
          >
            {t("Close")}
          </Button>
          <Tooltip
            title={hasAramexShipments ? '' : t("Only late shipments from Aramex can be sent by email.")}
            placement='top'
            disableHoverListener={hasAramexShipments}
          >
            <LoadingButton
              variant='contained'
              sx={{ textTransform: 'none' }}
              onClick={handleSendEmail}
              endIcon={<Email />}
              disabled={!hasAramexShipments || isSuccess}
            >
              {t("Send")}
            </LoadingButton>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </>
  )
}
